import {
	MediaQueryKind,
	useDataInputDialog,
	useIsTouchDevice,
	useWarnBeforeUnload,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { UnmanagedComposerDialog } from "./UnmanagedComposerDialog";
import {
	CommunityPostImageFormModelKind,
	CommunityPostImageFormData,
	createValidatorForEditPost,
	createValidatorForNewPost,
	EMPTY_POST_FORM_DATA,
	PostComposerFormData
} from "./common";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	initialMinimalEditorValue,
	RichTextEditorIntermediateStateHandlers,
	useConfirmationDialog,
	useRichTextEditorIntermediateState
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { isProseMirror, RichTextEditorState } from "client/Components/RichTextEditor/types";
import { getHtml } from "client/Components/RichTextEditor/htmlConversion";
import { useIsEmpty } from "./useIsEmpty";
import { never } from "client_legacy/ndaw/errors";
import { useSavePost } from "./useSavePost";
import {
	CommunityPostDetailsViewModel,
	CommunityPostImageViewModel,
	CommunityPostPollViewModel
} from "client/Api/Communities/communityPostViewer";
import { CommunityViewModel } from "client/Api/Communities/communityPosts";
import { zip } from "client_legacy/ndaw/arrayHelpers";
import { MAX_LINKED_DOCUMENTS_COUNT, PostComposerOptions } from "./NewPostComposer";
import { IValidationConfiguration } from "client/Validation";
import { isNotNil } from "client/Misc/utils";
import { ObjectWithClientId, setClientId } from "client/Misc/clientId";
import { useAddDocuments } from "../../useAddDocuments";
import { documentsConfiguration, WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { IValueInputAttachable } from "client/Forms/attachable";
import { DocumentStoreType } from "client/Api/Documents/common";
import { LinkedDocumentFormData } from "../../common";
import { HubLinkedDocumentViewModel } from "client/Api/Hub/common";

export type ComposerDialogProps = {
	mode: CommunityPostComposerMode;
	options: PostComposerOptions;
	onSaved: (post: CommunityPostDetailsViewModel) => void;
	onClose: () => void;
};

export const ComposerDialog = (props: ComposerDialogProps): ReactElement => {
	const {
		mode,
		options,
		onSaved,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<PostComposerFormData>(
		() => convertComposerModeToFormData(mode)
	);

	const [shouldShowMessageToolbar, setShouldShowMessageToolbar] = useState<boolean>(false);

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<PostComposerFormData> => {
			switch (mode.kind) {
				case "create":
					return createValidatorForNewPost(options);
				case "edit":
					return createValidatorForEditPost(
						options,
						isNotNil(mode.poll)
					);
				default:
					never(mode);
			}
		},
		[mode, options]
	);

	const formHandler: IFormHandler<PostComposerFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const editorHandlers: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState(
		initialMinimalEditorValue
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isTouch: boolean = useIsTouchDevice();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const { intermediateState } = editorHandlers;
	const isEmpty: boolean = useIsEmpty(formData, intermediateState);
	const isPostDifferent = useIsDifferent(
		formData,
		intermediateState,
		mode
	);

	const {
		isSharepointEnabled,
		webDavConfiguration
	} = useMemo(
		(): ({
			isSharepointEnabled: boolean;
			webDavConfiguration: WebDavConfiguration | null;
		}) => {
			const {
				sharePoint,
				webDav
			} = documentsConfiguration();

			return {
				isSharepointEnabled: sharePoint.isConfigured,
				webDavConfiguration: webDav
			};
		},
		[]
	);

	const linkedDocumentsAttachable: IValueInputAttachable<readonly ObjectWithClientId<LinkedDocumentFormData>[]> = formHandler
		.for("linkedDocuments");

	const linkedDocuments = useMemo(
		(): readonly ObjectWithClientId<LinkedDocumentFormData>[] => linkedDocumentsAttachable
			.value,
		[linkedDocumentsAttachable.value]
	);

	const selectionMadeHandler = useCallback(
		(itemsToAdd: readonly LinkedDocumentFormData[]) => {
			const currentItemsCount: number = linkedDocuments.length;
			const itemsToAddCount: number = itemsToAdd.length;

			if (itemsToAddCount + currentItemsCount > MAX_LINKED_DOCUMENTS_COUNT) {
				throw new Error(`The maximum number of allowed documents is ${MAX_LINKED_DOCUMENTS_COUNT}.`);
			}

			linkedDocumentsAttachable
				.reducer
				.setValue([
					...linkedDocuments,
					...itemsToAdd.map(setClientId)
				]);

			linkedDocumentsAttachable
				.validation
				.triggerInputFinished();
		},
		[
			linkedDocuments,
			linkedDocumentsAttachable.reducer,
			linkedDocumentsAttachable.validation
		]
	);

	const {
		add: addLinkedDocumentHandler,
		dialogElement: addLinkedDocumentDialog,
		openDocument: openLinkedDocument
	} = useAddDocuments({
		isSharepointEnabled,
		items: linkedDocuments,
		maxItemsCount: MAX_LINKED_DOCUMENTS_COUNT,
		onChangeIsLoading: setIsBusy,
		onSelect: selectionMadeHandler,
		webDavConfiguration
	});

	const isWebDavAvailable: boolean = isNotNil(webDavConfiguration)
		&& webDavConfiguration.isConfigured;

	const canAddLinkedDocuments: boolean = linkedDocuments.length < MAX_LINKED_DOCUMENTS_COUNT;

	const shouldWarnBeforeExit: boolean = !isBusy
		&& (
			(mode.kind === "create" && !isEmpty)
			|| (mode.kind === "edit" && isPostDifferent)
		);

	useWarnBeforeUnload(shouldWarnBeforeExit);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			if (shouldWarnBeforeExit) {
				const result = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToLeaveThisPage"),
					children: localize("_YouHaveUnsavedChanges"),
					confirmText: localize("_DiscardChanges"),
					cancelText: localize("_Cancel"),
					trackingId: "discard-community-post-form-confirmation-dialog"
				});

				if (!result.wasSuccessful) {
					return;
				}
			}

			onClose();
		},
		[
			onClose,
			openConfirmationDialog,
			shouldWarnBeforeExit
		]
	);

	const savePost: () => Promise<TryResult<CommunityPostDetailsViewModel>> = useSavePost(
		isBusy,
		setIsBusy,
		formHandler,
		isMobile
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (mode.kind === "edit" && !isPostDifferent) {
				onClose();
				return;
			}

			const result: TryResult<CommunityPostDetailsViewModel> = await savePost();

			if (!result.wasSuccessful) {
				return;
			}

			onSaved(result.result);
		},
		[
			isPostDifferent,
			mode.kind,
			onClose,
			onSaved,
			savePost
		]
	);

	const {
		onChange: onChangeEditorState,
		value: editorState,
		onBlur: onBlurEditor
	} = formHandler.for("message")
		.attachToRichTextEditor(editorHandlers);

	return (
		<>
			<UnmanagedComposerDialog
				{...mode.kind === "create"
					? {
						mode: "create",
						communities: mode.communities,
						initialCommunityId: mode.communityId
					}
					: {
						mode: "edit",
						community: mode.community,
						initialImages: mode.images,
						initialPoll: mode.poll
					}
				}
				attachable={formHandler}
				options={options}
				isMobile={isMobile}
				isTouch={isTouch}
				isBusy={isBusy}
				onIsBusyChange={setIsBusy}
				onClose={closeHandler}
				onChangeEditorState={onChangeEditorState}
				onBlurEditor={onBlurEditor}
				editorState={editorState}
				onSubmit={saveHandler}
				shouldShowMessageToolbar={shouldShowMessageToolbar}
				onShouldShowMessageToolbarChange={setShouldShowMessageToolbar}
				canAddLinkedDocuments={canAddLinkedDocuments}
				onAddLinkedDocument={addLinkedDocumentHandler}
				isWebDavAvailable={isWebDavAvailable}
				isSharepointEnabled={isSharepointEnabled}
				onOpenLinkedDocument={openLinkedDocument}
			/>
			{confirmationDialog}
			{addLinkedDocumentDialog}
		</>
	);
};
ComposerDialog.displayName = "ComposerDialog";

export type ComposerDialogInitialData = Omit<
	ComposerDialogProps,
	"onClose" | "onSaved"
>;

export function useComposerDialog(): [
	element: ReactElement | null,
	open: (props: ComposerDialogInitialData) => Promise<TryResult<CommunityPostDetailsViewModel>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<ComposerDialog
				{...props}
				onSaved={onSave}
				onClose={onClose}
			/>
		)
	);
}

export type CommunityPostComposerMode = {
	kind: "create";
	communityId: number | null;
	communities: readonly CommunityViewModel[];
} | {
	kind: "edit";
	message: string;
	community: CommunityViewModel;
	id: number;
	images: readonly CommunityPostImageViewModel[];
	poll: CommunityPostPollViewModel | null;
	linkedDocuments: readonly HubLinkedDocumentViewModel[];
};

function convertComposerModeToFormData(
	model: CommunityPostComposerMode
): PostComposerFormData {
	switch (model.kind) {
		case "create":
			return {
				...EMPTY_POST_FORM_DATA,
				communityId: model.communityId
			};
		case "edit":
			return {
				message: model.message,
				images: model.images
					.map(
						(image): ObjectWithClientId<CommunityPostImageFormData> => setClientId({
							kind: CommunityPostImageFormModelKind.Existing,
							id: image.id
						})
					),
				communityId: model.community
					.id,
				id: model.id,
				poll: { isSet: false },
				linkedDocuments: model
					.linkedDocuments
					.map(
						x => setClientId(convertToLinkedDocumentFormData(x))
					)
			};
		default:
			never(model);
	}
}

function convertToLinkedDocumentFormData(
	item: HubLinkedDocumentViewModel
): LinkedDocumentFormData {
	switch (item.documentStoreType) {
		case DocumentStoreType.SharePoint:
			return {
				documentStoreType: DocumentStoreType.SharePoint,
				itemId: item.itemId,
				location: item.location,
				name: item.name,
				extension: item.extension
			};
		case DocumentStoreType.WebDav:
			return {
				documentStoreType: DocumentStoreType.WebDav,
				fileId: item.fileId,
				serverId: item.serverId,
				name: item.name,
				extension: item.extension
			};
		default:
			never(item);
	}
}

function useIsDifferent(
	formData: PostComposerFormData,
	intermediateState: RichTextEditorState,
	mode: CommunityPostComposerMode
): boolean {
	return useMemo(
		(): boolean => {
			if (mode.kind === "edit") {
				if (!isProseMirror(intermediateState)) {
					throw new Error("Editor not supported");
				}

				const {
					images,
					poll,
					linkedDocuments
				} = formData;

				const areImagesChanged: boolean = !areSameImages(
					images,
					mode.images
				);

				const areLinkedDocumentsChanged: boolean = !areSameLinkedDocuments(
					linkedDocuments,
					mode.linkedDocuments
				);

				if (areImagesChanged
					|| poll.isSet
					|| areLinkedDocumentsChanged
				) {
					return true;
				}

				const message: string = getHtml(
					intermediateState.editorState
				);

				return message !== mode.message;
			}

			return false;
		},
		[
			mode,
			intermediateState,
			formData
		]
	);
}

function areSameImages(
	currentImages: readonly ObjectWithClientId<CommunityPostImageFormData>[],
	initialImages: readonly CommunityPostImageViewModel[]
): boolean {
	if (currentImages.length !== initialImages.length) {
		return false;
	}

	return zip(currentImages, initialImages)
		.every(
			([currentImage, initialImage]) => {
				return currentImage.kind === CommunityPostImageFormModelKind.Existing
					&& currentImage.id === initialImage.id;
			}
		);
}

function areSameLinkedDocuments(
	currentLinkedDocuments: readonly ObjectWithClientId<LinkedDocumentFormData>[],
	initialLinkedDocuments: readonly HubLinkedDocumentViewModel[]
): boolean {
	if (currentLinkedDocuments.length !== initialLinkedDocuments.length) {
		return false;
	}

	return zip(currentLinkedDocuments, initialLinkedDocuments)
		.every(
			([currentItem, initialItem]) => {
				switch (currentItem.documentStoreType) {
					case DocumentStoreType.SharePoint:
						return currentItem.documentStoreType === initialItem.documentStoreType
							&& currentItem.itemId === initialItem.itemId
							&& currentItem.location.listId === initialItem.location.listId
							&& currentItem.location.siteUrl === initialItem.location.siteUrl;
					case DocumentStoreType.WebDav:
						return currentItem.documentStoreType === initialItem.documentStoreType
							&& currentItem.fileId === initialItem.fileId
							&& currentItem.serverId === initialItem.serverId;
					default:
						return never(currentItem);
				}
			}
		);
}
